import { supabase } from './supabase';
import type { Showcase, ShowcaseProperty, ShowcaseResource, ShowcaseSeo, PublicShowcaseData, ShowcaseTemplate, ShowcaseHostingPlan } from '../types';
import type { SectionType } from '../constants';
import type { HostingPlanId } from '@rehub/pricing';
import slugifyLib from 'slugify';

export interface ShowcaseWithHero extends Showcase {
  hero_url?: string | null;
}

export async function fetchShowcases(userId: string): Promise<ShowcaseWithHero[]> {
  const { data, error } = await supabase
    .from('showcases')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  const showcases = data || [];

  const heroUrls = await Promise.all(
    showcases.map(async (s) => {
      const { data: res } = await supabase
        .from('showcase_resources')
        .select('source_url, thumbnail_url')
        .eq('showcase_id', s.id)
        .eq('type', 'hero_image')
        .limit(1)
        .single();
      return res?.thumbnail_url || res?.source_url || null;
    })
  );

  return showcases.map((s, i) => ({ ...s, hero_url: heroUrls[i] }));
}

export async function getShowcase(id: string): Promise<Showcase | null> {
  const { data, error } = await supabase
    .from('showcases')
    .select('*')
    .eq('id', id)
    .single();

  if (error) return null;
  return data;
}

export async function getShowcaseProperty(showcaseId: string): Promise<ShowcaseProperty | null> {
  const { data, error } = await supabase
    .from('showcase_properties')
    .select('*')
    .eq('showcase_id', showcaseId)
    .single();

  if (error) return null;
  return data;
}

export async function getShowcaseResources(showcaseId: string): Promise<ShowcaseResource[]> {
  const { data, error } = await supabase
    .from('showcase_resources')
    .select('*')
    .eq('showcase_id', showcaseId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getShowcaseSeo(showcaseId: string): Promise<ShowcaseSeo | null> {
  const { data, error } = await supabase
    .from('showcase_seo')
    .select('*')
    .eq('showcase_id', showcaseId)
    .single();

  if (error) return null;
  return data;
}

export async function createShowcase(
  userId: string,
  title: string,
  templateId: string,
  teamId?: string | null
): Promise<string> {
  const slug = slugifyLib(title, { lower: true, strict: true }) + '-' + Math.random().toString(36).slice(2, 8);

  const { data, error } = await supabase
    .from('showcases')
    .insert({
      user_id: userId,
      team_id: teamId || null,
      title,
      slug,
      status: 'draft',
      template_id: templateId,
      section_order: [],
      enabled_sections: [],
    })
    .select('id')
    .single();

  if (error) throw error;

  await supabase.from('showcase_properties').insert({ showcase_id: data.id });
  await supabase.from('showcase_seo').insert({ showcase_id: data.id });

  return data.id;
}

export async function updateShowcase(id: string, updates: Partial<Showcase>): Promise<void> {
  const { error } = await supabase
    .from('showcases')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
}

export async function updateShowcaseProperty(
  showcaseId: string,
  updates: Partial<ShowcaseProperty>
): Promise<void> {
  const { error } = await supabase
    .from('showcase_properties')
    .update(updates)
    .eq('showcase_id', showcaseId);

  if (error) throw error;
}

export async function updateShowcaseSeo(
  showcaseId: string,
  updates: Partial<ShowcaseSeo>
): Promise<void> {
  const { error } = await supabase
    .from('showcase_seo')
    .upsert({ showcase_id: showcaseId, ...updates });

  if (error) throw error;
}

export async function deleteShowcase(id: string): Promise<void> {
  await supabase.from('showcase_resources').delete().eq('showcase_id', id);
  await supabase.from('showcase_properties').delete().eq('showcase_id', id);
  await supabase.from('showcase_seo').delete().eq('showcase_id', id);
  await supabase.from('showcase_leads').delete().eq('showcase_id', id);
  await supabase.from('showcase_ai_sessions').delete().eq('showcase_id', id);

  const { error } = await supabase.from('showcases').delete().eq('id', id);
  if (error) throw error;
}

export async function duplicateShowcase(id: string): Promise<string | null> {
  const original = await getShowcase(id);
  if (!original) return null;

  const property = await getShowcaseProperty(id);
  const resources = await getShowcaseResources(id);
  const seo = await getShowcaseSeo(id);

  const newSlug = original.slug + '-copy-' + Math.random().toString(36).slice(2, 6);

  const { data: newShowcase, error } = await supabase
    .from('showcases')
    .insert({
      user_id: original.user_id,
      team_id: original.team_id,
      title: original.title + ' (Copy)',
      slug: newSlug,
      status: 'draft',
      template_id: original.template_id,
      primary_brand_colour: original.primary_brand_colour,
      logo_url: original.logo_url,
      contact_name: original.contact_name,
      contact_email: original.contact_email,
      contact_phone: original.contact_phone,
      office_name: original.office_name,
      office_address: original.office_address,
      agent_id: original.agent_id,
      notify_agent: original.notify_agent,
      notify_owner: original.notify_owner,
      notify_emails: original.notify_emails,
      section_order: original.section_order,
      enabled_sections: original.enabled_sections,
    })
    .select('id')
    .single();

  if (error || !newShowcase) return null;

  if (property) {
    const { showcase_id: _, ...propData } = property;
    await supabase.from('showcase_properties').insert({ showcase_id: newShowcase.id, ...propData });
  }

  if (seo) {
    const { showcase_id: _, ...seoData } = seo;
    await supabase.from('showcase_seo').insert({ showcase_id: newShowcase.id, ...seoData });
  }

  for (const resource of resources) {
    const { id: _id, showcase_id: _sid, created_at: _ca, updated_at: _ua, ...resData } = resource;
    await supabase.from('showcase_resources').insert({ showcase_id: newShowcase.id, ...resData });
  }

  return newShowcase.id;
}

export interface PublishValidation {
  canPublish: boolean;
  errors: string[];
  warnings: string[];
}

export function validateForPublish(
  showcase: Showcase,
  property: ShowcaseProperty | null,
  resources: ShowcaseResource[]
): PublishValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!showcase.title?.trim()) errors.push('Title is required');
  if (!showcase.slug?.trim()) errors.push('URL slug is required');

  const heroImages = resources.filter((r) => r.type === 'hero_image');
  if (heroImages.length === 0) errors.push('At least one hero image is required');

  if (!showcase.contact_name && !showcase.contact_email) {
    warnings.push('No contact details provided');
  }

  const hasGallery = resources.some((r) => r.type === 'gallery' || r.type === 'image');
  if (!hasGallery) warnings.push('No gallery images added');

  const hasFloorPlan = resources.some((r) => r.type === 'floorplan_pdf' || r.type === 'planos_embed');
  if (!hasFloorPlan) warnings.push('No floor plan attached');

  return {
    canPublish: errors.length === 0,
    errors,
    warnings,
  };
}

export interface PublishResult {
  plan: ShowcaseHostingPlan;
  expiresAt: string | null;
  creditsCharged: number;
}

export async function publishShowcase(id: string, plan: HostingPlanId): Promise<PublishResult> {
  const { data, error } = await supabase.rpc('publish_showcase_with_plan', {
    p_showcase_id: id,
    p_plan: plan,
  });

  if (error) throw error;

  const result = data as {
    success: boolean;
    error?: string;
    active_count?: number;
    limit?: number;
    required?: number;
    available?: number;
    plan?: ShowcaseHostingPlan;
    expires_at?: string | null;
    credits_charged?: number;
  };
  if (!result.success) {
    if (result.error === 'SLOT_LIMIT_REACHED') {
      throw new Error(`Active space limit reached (${result.active_count}/${result.limit}). Upgrade your plan or unpublish another item.`);
    }
    if (result.error === 'INSUFFICIENT_CREDITS') {
      throw new Error(`Not enough credits to publish. This duration needs ${result.required} credits and you have ${result.available}.`);
    }
    throw new Error(result.error || 'Failed to publish');
  }

  return {
    plan: result.plan ?? (plan as ShowcaseHostingPlan),
    expiresAt: result.expires_at ?? null,
    creditsCharged: result.credits_charged ?? 0,
  };
}

export async function unpublishShowcase(id: string): Promise<void> {
  const { error } = await supabase
    .from('showcases')
    .update({
      status: 'draft',
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) throw error;
}

export async function getPublicShowcase(slug: string): Promise<PublicShowcaseData | null> {
  const { data, error } = await supabase.rpc('get_public_showcase_by_slug', { p_slug: slug });

  if (error || !data) return null;
  return data as unknown as PublicShowcaseData;
}

export async function updateSectionOrder(id: string, sectionOrder: SectionType[]): Promise<void> {
  await updateShowcase(id, { section_order: sectionOrder } as Partial<Showcase>);
}

export async function updateEnabledSections(id: string, enabledSections: SectionType[]): Promise<void> {
  await updateShowcase(id, { enabled_sections: enabledSections } as Partial<Showcase>);
}
